import { FilterOption, filter } from '@zspace/data';
import {
  PaginatedContentConfig,
  SalesOrder,
  SalesOrderSoftwareTitlesTableRow,
} from '@zspace/types';
import { useCallback, useMemo, useState } from 'react';
import { Element } from 'react-bulma-components';
import Conditional from '../../shared/conditional/conditional';
import useIsPartnerUser from '../../shared/hooks/is-partner-user';
import InboxTable from '../../ui/inbox-table/inbox-table';
import Link from '../../ui/link/link';
import InfiniteTable from '../../ui/table/infinite-table/infinite-table';
import { Column } from '../../ui/table/types';

const filterOptions: FilterOption<SalesOrderSoftwareTitlesTableRow>[] = [
  {
    getter: (el) => el.id,
    searchable: false,
  },
  {
    getter: (el) => el.title,
    searchable: true,
  },
  {
    getter: (el) => String(el.seats),
    searchable: false,
  },
  {
    getter: (el) => String(el.type),
    searchable: true,
  },
];

export interface SalesOrderSoftwareTitlesTableProps {
  data: SalesOrderSoftwareTitlesTableRow[];
  salesOrder: Pick<SalesOrder, 'number'>;
}

const ITEMS_PER_PAGE = 9;

export function SalesOrderSoftwareTitleTable({
  data,
  salesOrder,
}: SalesOrderSoftwareTitlesTableProps) {
  const isPartnerUser = useIsPartnerUser();
  const [tableData, setTableData] = useState<
    SalesOrderSoftwareTitlesTableRow[]
  >(data.slice(0, ITEMS_PER_PAGE));

  const [tableConfig, setTableConfig] = useState<PaginatedContentConfig>({
    itemsPerPage: ITEMS_PER_PAGE,
    pageNumber: 1,
    pages: Math.ceil(data.length / ITEMS_PER_PAGE),
    search: '',
    sortBy: '',
  });

  const emptyContent = useMemo(() => {
    const hasAppliedFilters = tableConfig.search !== '';
    let emptyContentText = 'This sales order does not contain any software';
    if (hasAppliedFilters) {
      emptyContentText =
        'There are no software titles that match the selected criteria';
    }
    return (
      <Element display="flex" justifyContent="center" className="my-2">
        <span className="my-2 has-text-weight-light">{emptyContentText}</span>
      </Element>
    );
  }, [tableConfig.search]);

  const columns: Column<SalesOrderSoftwareTitlesTableRow>[] = useMemo(
    () => [
      {
        key: 'title',
        render: (el) => (
          <Conditional condition={isPartnerUser}>
            <Conditional.True>
              <span>{el.title}</span>
            </Conditional.True>
            <Conditional.False>
              <Link
                to={`/software/${el.id}?${new URLSearchParams({
                  salesOrders: salesOrder.number.toString(),
                })}`}
                openNewTab
              >
                {el.title}
              </Link>
            </Conditional.False>
          </Conditional>
        ),
        className: 'is-width-60',
        title: 'Software title',
        sortable: false,
      },
      {
        key: 'seats',
        className: 'is-width-20',
        render: (el) => el.seats,
        title: 'Seats',
        sortable: false,
      },
      {
        key: 'type',
        className: 'is-width-20',
        render: (el) => el.type,
        title: 'Software type',
        sortable: false,
      },
    ],
    [isPartnerUser, salesOrder.number]
  );

  const fetchMoreSoftwareTitles = useCallback(async () => {
    const newTableConfig = {
      ...tableConfig,
      pageNumber: tableConfig.pageNumber + 1,
    };
    const filteredData = filter(data, newTableConfig, filterOptions);
    const newTableData = filteredData.slice(
      (newTableConfig.pageNumber - 1) * newTableConfig.itemsPerPage,
      newTableConfig.pageNumber * newTableConfig.itemsPerPage
    );

    newTableConfig.pages = Math.ceil(
      filteredData.length / newTableConfig.itemsPerPage
    );

    setTableData((prevTableData) => prevTableData.concat(newTableData));
    setTableConfig(newTableConfig);
  }, [data, tableConfig]);

  return (
    <InboxTable
      table={
        <InfiniteTable
          dataSource={tableData}
          columns={columns}
          fetchMore={fetchMoreSoftwareTitles}
          hasMore={tableData.length < data.length}
          config={tableConfig}
          rowKey={'key'}
          empty={emptyContent}
        />
      }
      config={tableConfig}
      enableSearch={false}
    />
  );
}

export default SalesOrderSoftwareTitleTable;
